import { useMemo, useState } from "react";
import { CheckCircle2, FileSearch, Languages, Search, ShieldCheck, Sparkles, Wand2 } from "lucide-react";
import { ToolCard } from "../components/ToolCard";
import { getHomeCopy } from "../data/i18n";
import { categoryLabel, getLocalizedTools } from "../data/tools";
import { applyPageSeo } from "../lib/seo";
import { homePath } from "../lib/routing";
import type { Locale, ToolDefinition, ToolId } from "../types";

type HomePageProps = {
  locale: Locale;
};

type WorkflowGroup = {
  id: string;
  title: string;
  body: string;
  toolIds: ToolId[];
};

const workflowGroups: Record<"en" | "zh", WorkflowGroup[]> = {
  en: [
    {
      id: "polish",
      title: "Fix and polish a draft",
      body: "Check weak phrasing, grammar, readability, and the parts that make text hard to trust.",
      toolIds: ["ai-text-checker", "grammar-fixer", "readability-improver"]
    },
    {
      id: "rewrite",
      title: "Rewrite for a real audience",
      body: "Naturalize, change tone, translate, or make a version that fits the reader.",
      toolIds: ["ai-humanizer", "tone-converter", "translator"]
    },
    {
      id: "review",
      title: "Summarize and verify",
      body: "Summarize long text, check similarity, format citations, and compare output quality.",
      toolIds: ["summary-generator", "similarity-checker", "citation-generator", "quality-comparison"]
    }
  ],
  zh: [
    {
      id: "polish",
      title: "修正并打磨草稿",
      body: "检查套话、语法、可读性，以及会削弱可信度的表达问题。",
      toolIds: ["ai-text-checker", "grammar-fixer", "readability-improver"]
    },
    {
      id: "rewrite",
      title: "按真实读者改写",
      body: "自然化、调整语气、翻译，或生成更适合目标读者的版本。",
      toolIds: ["ai-humanizer", "tone-converter", "translator"]
    },
    {
      id: "review",
      title: "总结与核查",
      body: "总结长文本、检查相似度、生成引用，并对比不同输出质量。",
      toolIds: ["summary-generator", "similarity-checker", "citation-generator", "quality-comparison"]
    }
  ]
};

function localizedGroups(locale: Locale) {
  return locale === "zh" ? workflowGroups.zh : workflowGroups.en;
}

function filterTools(tools: ToolDefinition[], query: string) {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return tools;
  }

  return tools.filter((tool) => `${tool.name} ${tool.shortName} ${tool.tagline} ${tool.description}`.toLowerCase().includes(normalizedQuery));
}

export function HomePage({ locale }: HomePageProps) {
  const copy = getHomeCopy(locale);
  const tools = getLocalizedTools(locale);
  const [query, setQuery] = useState("");
  const visibleTools = useMemo(() => filterTools(tools, query), [tools, query]);
  const groups = localizedGroups(locale);
  const highlightedTools = tools.filter((tool) => ["ai-text-checker", "ai-humanizer", "grammar-fixer"].includes(tool.id));
  const textChecker = tools.find((tool) => tool.id === "ai-text-checker") ?? tools[0];

  applyPageSeo({
    title: copy.title as string,
    description: copy.metaDescription as string,
    locale,
    path: homePath(locale)
  });

  return (
    <main id="main-content">
      <section className="home-hero">
        <div className="home-hero__copy">
          <p className="eyebrow">{copy.eyebrow}</p>
          <h1>{copy.h1}</h1>
          <p>{copy.lead}</p>
          <div className="hero-actions">
            <a className="primary-button" href={textChecker.path}>
              <Sparkles size={17} aria-hidden="true" />
              {textChecker.primaryAction}
            </a>
            <a className="secondary-button" href="#tools">
              {copy.toolSection}
            </a>
          </div>
          <div className="proof-strip" aria-label="Product proof points">
            {(copy.proof as string[]).map((proof) => (
              <span key={proof}>{proof}</span>
            ))}
          </div>
        </div>
        <div className="hero-panel hero-editor" aria-label={copy.workflowTitle as string}>
          <div className="hero-editor__toolbar">
            {highlightedTools.map((tool) => (
              <a key={tool.id} href={tool.path}>
                {tool.shortName}
              </a>
            ))}
          </div>
          <div className="hero-editor__surface">
            <div className="hero-editor__draft" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
            </div>
            <p>{locale === "zh" ? "这段文字有点泛泛而谈，需要更清楚、更像真实表达。" : "This draft is generic and needs clearer, more natural wording."}</p>
            <div className="hero-editor__suggestion">
              <strong>{locale === "zh" ? "建议" : "Suggestion"}</strong>
              <span>{locale === "zh" ? "把泛泛表述改成具体、可读、可发布的版本。" : "Make the wording specific, readable, and ready to use."}</span>
            </div>
            <ul>
              <li>
                <CheckCircle2 size={16} aria-hidden="true" />
                {locale === "zh" ? "保留原意" : "Meaning preserved"}
              </li>
              <li>
                <Wand2 size={16} aria-hidden="true" />
                {locale === "zh" ? "减少套话" : "Generic phrasing reduced"}
              </li>
              <li>
                <Languages size={16} aria-hidden="true" />
                {locale === "zh" ? "适配主流语种" : "Major-language workflow"}
              </li>
            </ul>
          </div>
          <div className="hero-editor__footer">
            <span>{copy.workflowTitle}</span>
            <a href={textChecker.path}>{locale === "zh" ? "开始检查" : "Start checking"}</a>
          </div>
        </div>
      </section>
      <section className="workflow-grid" aria-label={locale === "zh" ? "常见写作任务" : "Common writing jobs"}>
        {groups.map((group) => (
          <article key={group.id} className="workflow-card">
            <h2>{group.title}</h2>
            <p>{group.body}</p>
            <div>
              {group.toolIds
                .map((toolId) => tools.find((tool) => tool.id === toolId))
                .filter((tool): tool is ToolDefinition => Boolean(tool))
                .map((tool) => (
                  <a key={tool.id} href={tool.path}>
                    {tool.shortName}
                  </a>
                ))}
            </div>
          </article>
        ))}
      </section>
      <section className="section-heading" id="tools">
        <div>
          <p className="eyebrow">{copy.allTools}</p>
          <h2>{copy.toolSection}</h2>
        </div>
        <form className="search-box" role="search">
          <Search size={20} aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label={copy.searchLabel as string}
            placeholder={copy.searchPlaceholder as string}
          />
        </form>
      </section>
      <p className="tool-count" aria-live="polite">
        {locale === "zh" ? `显示 ${visibleTools.length} 个工具` : `${visibleTools.length} tools shown`}
      </p>
      <section className="tool-grid" aria-label="Available tools">
        {visibleTools.map((tool) => (
          <ToolCard key={tool.id} tool={tool} locale={locale} />
        ))}
      </section>
      <section className="insight-band">
        <div>
          <FileSearch size={22} aria-hidden="true" />
          <h2>{copy.geoTitle}</h2>
          <p>{copy.geoBody}</p>
          <div className="insight-links">
            {tools.slice(0, 3).map((tool) => (
              <a key={tool.id} href={tool.path}>
                {tool.shortName}
              </a>
            ))}
          </div>
        </div>
        <div>
          <ShieldCheck size={22} aria-hidden="true" />
          <h2>{locale === "zh" ? "发布前请再看一眼" : "Review before you publish"}</h2>
          <p>
            {locale === "zh"
              ? "AI 可以帮你改写、压缩和调整语气，但最终内容仍需要你确认事实、语境和表达是否合适。"
              : "AI can help rewrite, shorten, and adjust tone, but you should still check the facts, context, and final wording."}
          </p>
          <div className="insight-links">
            <span>{locale === "zh" ? "人工确认" : "Human review"}</span>
            <span>{locale === "zh" ? "本地指标" : "Local metrics"}</span>
          </div>
        </div>
      </section>
    </main>
  );
}
